/**
 * Query Telemetry — captures timing data and sends to the Worker for D1 storage.
 *
 * Timing labels: voice, ai, data, render
 * Uses navigator.sendBeacon() for reliability (survives page navigation).
 */
import globeContext from '../state/globe-context.js'
import { licenseHeaders } from './license.js'

const API_URL = import.meta.env.VITE_API_URL || ''

// ── Timer state ──────────────────────────────────────────────
const timers = new Map() // label → startTime (ms)
const timings = new Map() // label → duration (ms)

export function startTimer(label) {
  timers.set(label, performance.now())
}

export function endTimer(label) {
  const start = timers.get(label)
  if (start != null) {
    timings.set(label, Math.round(performance.now() - start))
    timers.delete(label)
  }
}

export function getTimings() {
  const result = {}
  for (const [label, duration] of timings) {
    result[`time_${label}_ms`] = duration
  }
  return result
}

export function clearTimings() {
  timers.clear()
  timings.clear()
}

// ── Release-stage observation tagging (CL-163, roadmap H2.4) ─
// While a lesson's freeform ("Release") step is active, every query the
// student makes is an invisible-assessment observation: main.js sets the
// lesson context here, and sendTelemetry stamps it onto each payload —
// covering fast-path queries that never touch /api/intent. Cleared the
// moment the lesson leaves the freeform state.

let _lessonObservation = null

/**
 * @param {{ lessonId: string, stepId?: string, concepts?: string[] }|null} ctx
 */
export function setLessonObservation(ctx) {
  _lessonObservation = ctx && ctx.lessonId ? ctx : null
}

export function getLessonObservation() {
  return _lessonObservation
}

/**
 * Send the telemetry payload to the Worker.
 * Fire-and-forget — uses sendBeacon for reliability, falls back to fetch.
 */
export function sendTelemetry({
  transcript,
  inputMethod = 'typed',
  intentType,
  intentTarget,
  contextAction,
  resultStatus = 'success',
  resultSummary,
  featureCount,
  datasetUsed,
}) {
  const ctx = globeContext.getContext()
  const timing = getTimings()

  const payload = {
    session_id: ctx.session_id,
    chain_id: ctx.chain_id,
    chain_depth: ctx.chain_depth,
    query_index: ctx.query_index,
    transcript,
    input_method: inputMethod,
    globe_context: globeContext.serialize(),
    ...timing,
    time_total_ms:
      (timing.time_voice_ms || 0) +
      (timing.time_ai_ms || 0) +
      (timing.time_data_ms || 0) +
      (timing.time_render_ms || 0),
    intent_type: intentType,
    intent_target: intentTarget,
    context_action: contextAction,
    result_status: resultStatus,
    result_summary: resultSummary,
    feature_count: featureCount,
    dataset_used: datasetUsed,
  }

  // CL-163 (H2.4): stamp release-stage observation tags while a lesson's
  // freeform step is active.
  if (_lessonObservation) {
    payload.lesson_id = _lessonObservation.lessonId
    if (_lessonObservation.stepId) payload.lesson_step_id = _lessonObservation.stepId
    if (Array.isArray(_lessonObservation.concepts) && _lessonObservation.concepts.length > 0) {
      payload.lesson_concepts = _lessonObservation.concepts
    }
  }

  // Clear timings for next query
  clearTimings()

  const url = `${API_URL}/api/telemetry`
  const body = JSON.stringify(payload)
  const license = licenseHeaders()

  // Use sendBeacon for reliability (survives page navigation). sendBeacon
  // can't set custom headers, so licensed sessions use fetch keepalive
  // instead (same survives-navigation guarantee) to carry X-EV-License
  // for per-school attribution (CL-131).
  if (navigator.sendBeacon && !license['X-EV-License']) {
    const blob = new Blob([body], { type: 'application/json' })
    const sent = navigator.sendBeacon(url, blob)
    if (!sent) {
      // Fallback to fetch if sendBeacon fails (e.g., payload too large)
      fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body }).catch(
        () => {}
      ) // Swallow errors — telemetry is best-effort
    }
  } else {
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...license },
      body,
      keepalive: true,
    }).catch(() => {})
  }
}

const telemetry = {
  startTimer,
  endTimer,
  getTimings,
  clearTimings,
  sendTelemetry,
  setLessonObservation,
  getLessonObservation,
}

export default telemetry
